<template>
    <RouterLink
        class="rc-card"
        :class="{ 'rc-card_overdue': isOverdue }"
        :style="{ '--rc-card-state-color': stateColor, '--list-text-lines': textLines }"
        :to="routerRequestLink"
    >
        <div class="rc-card__top">
            <span class="rc-card__id">№ {{ request.id }}</span>
            <RequestPriorityBadge :priority="request.priority"></RequestPriorityBadge>
            <span class="rt-pill" :class="'rt-pill_' + cardStatus.className">{{ cardStatus.title }}</span>
            <span class="rt-pill rt-pill_unread" v-if="cardUnread > 0">{{ cardUnread }}</span>
            <span class="rc-card__deadline" :class="'rc-card__deadline_' + deadlineClass" :title="deadlineTitle">{{
                deadlineText
            }}</span>
        </div>

        <p class="rc-card__description" :title="cardText">{{ cardText }}</p>

        <div class="rc-card__bottom">
            <div class="rc-card__user">
                <img class="rc-card__avatar" :src="avatarUrl(request.author)" v-if="avatarUrl(request.author)" alt="" />
                <span class="rc-card__avatar rc-card__avatar_initials" v-else>{{
                    initials(request.author && request.author.displayName)
                }}</span>
                <span class="rc-card__user-name" :title="request.author && request.author.displayName">{{
                    (request.author && request.author.displayName) || '—'
                }}</span>
            </div>

            <span class="rc-card__arrow">→</span>

            <div class="rc-card__user" v-if="request.executor">
                <img
                    class="rc-card__avatar"
                    :src="avatarUrl(request.executor)"
                    v-if="avatarUrl(request.executor)"
                    alt=""
                />
                <span class="rc-card__avatar rc-card__avatar_initials" v-else>{{
                    initials(request.executor.displayName)
                }}</span>
                <span class="rc-card__user-name" :title="request.executor.displayName">{{
                    request.executor.displayName
                }}</span>
            </div>
            <div class="rc-card__user" v-else>
                <span class="rc-card__user-name" :title="request.securityGroup && request.securityGroup.name">{{
                    request.securityGroup ? request.securityGroup.name : '—'
                }}</span>
            </div>

            <span class="rc-card__created">создано {{ createdText }}</span>
        </div>
    </RouterLink>
</template>
<script>
import {
    description,
    lastMessage,
    statusView,
    unreadCount,
    deadlineView,
    stateColor as requestStateColor,
    avatarUrl,
    initials,
} from '../RequestTable/requestRowData';
import RequestPriorityBadge from '../RequestPriority/RequestPriorityBadge.vue';

export default {
    components: { RequestPriorityBadge },
    props: {
        request: { type: Object, required: true },
        textLines: { type: Number, default: 1 },
        textColumnSource: { type: String, default: 'description' },
    },
    computed: {
        stateColor() {
            return requestStateColor(this.request);
        },
        routerRequestLink() {
            return {
                name: 'request',
                params: {
                    id: this.request.id,
                    method: this.$route.params.method,
                },
            };
        },
        // Та же настройка источника текста, что и в таблице (см. RequestTableRow.vue) —
        // список один и тот же, вид переключается отдельно.
        cardText() {
            return this.textColumnSource === 'last-message' ? lastMessage(this.request) : description(this.request);
        },
        cardStatus() {
            return statusView(this.request);
        },
        cardUnread() {
            return unreadCount(this.request, this.$_account && this.$_account.id);
        },
        createdText() {
            return this.$_toLocalDateShort(this.request.createdAt);
        },
        cardDeadline() {
            return deadlineView(this.request, this.$_toLocalDateShort);
        },
        deadlineText() {
            return this.cardDeadline.text;
        },
        deadlineClass() {
            return this.cardDeadline.className;
        },
        deadlineTitle() {
            return this.cardDeadline.title;
        },
        // То же условие, что и в строке таблицы: список в карточках — тот же список,
        // и просрочка в нём должна читаться так же.
        isOverdue() {
            return this.deadlineClass === 'over';
        },
    },
    methods: {
        avatarUrl,
        initials,
    },
};
</script>
<style lang="scss">
.rc-card {
    display: block;
    // 15px по бокам — тот же отступ, что у футера списка под карточками.
    margin: 0 15px 8px;
    padding: 12px 14px;
    border: 1px solid #e3e5e8;
    // Полоса состояния — левая граница карточки: скругляется вместе с ней.
    border-left: 3px solid var(--rc-card-state-color, #e3e5e8);
    border-radius: 12px;
    text-decoration: none;
    color: inherit;
    transition: background-color 0.12s ease-out;

    &:hover {
        background: #f6f7f8;
    }

    // Просроченная заявка заливается целиком — те же цвета, что и у строки таблицы.
    // Стоит после :hover и до .router-link-exact-active: специфичность у всех троих
    // одинаковая, и порядок задаёт приоритет — открытая карточка перекрывает заливку,
    // наведение её только усиливает. Левую границу не трогаем: это полоса состояния.
    &_overdue {
        background: #fdecea;
        border-top-color: #f7d4d0;
        border-right-color: #f7d4d0;
        border-bottom-color: #f7d4d0;

        &:hover {
            background: #fbe0dd;
        }
    }

    // Карточка сама является RouterLink, поэтому класс открытого обращения приходит
    // прямо на неё. Оформление то же, что у строки таблицы: холодная заливка вне
    // статусной палитры.
    &.router-link-exact-active {
        background: #eef2f8;
        // Только три стороны: левая граница — это полоса состояния, и её цвет
        // принадлежит статусу заявки. `border-color` перекрасил бы и её тоже.
        border-top-color: #c9d4e2;
        border-right-color: #c9d4e2;
        border-bottom-color: #c9d4e2;
        color: #202124;

        &:hover {
            background: #e3ebf7;
        }

        .rc-card__id {
            color: #3c4043;
            font-weight: 500;
        }
    }

    // Перенос — на случай крупного текста в узкой панели: метки уходят на вторую
    // строку, а не налезают друг на друга и на дедлайн.
    &__top {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        row-gap: 4px;
    }

    &__id {
        margin-right: 8px;
        // Табличные цифры (tnum есть в Factor B): моношрифта в проекте нет.
        font: 400 0.8125rem/1 var(--font-family);
        font-variant-numeric: tabular-nums;
        color: #5f6368;
    }

    &__deadline {
        margin-left: auto;
        font: 400 0.8125rem/1 var(--font-family);
        color: #3c4043;

        &_over {
            color: #d93025;
            font-weight: 600;
        }

        &_soon {
            color: #d9641e;
            font-weight: 600;
        }

        &_dim {
            color: #9aa0a6;
        }
    }

    // --list-text-lines — та же переменная и тот же механизм (line-clamp), что и в
    // таблице: одно значение настройки должно выглядеть одинаково усечённым что тут,
    // что там, а не как два независимых поведения, которые могут разойтись.
    --list-line-height: 1.3;

    &__description {
        overflow: hidden;
        margin: 8px 0;
        white-space: normal;
        overflow-wrap: anywhere;
        font: italic 400 0.8125rem/1.3 var(--font-family);
        line-height: var(--list-line-height);
        color: #5f6368;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: var(--list-text-lines, 1);
        line-clamp: var(--list-text-lines, 1);

        @supports not (-webkit-line-clamp: 1) {
            max-height: calc(var(--list-text-lines, 1) * var(--list-line-height) * 1em);
        }
    }

    &__bottom {
        display: flex;
        align-items: center;
    }

    &__user {
        display: flex;
        align-items: center;
        min-width: 0;
        max-width: 40%;
    }

    &__avatar {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        margin-right: 8px;
        border-radius: 50%;
        object-fit: cover;

        &_initials {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #e8eaed;
            color: #5f6368;
            font-size: 10px;
            font-weight: 600;
        }
    }

    &__user-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font: 400 0.8125rem/1.3 var(--font-family);
        color: #3c4043;
    }

    &__arrow {
        flex-shrink: 0;
        margin: 0 8px;
        color: #c9ced3;
    }

    &__created {
        flex-shrink: 0;
        margin-left: auto;
        padding-left: 8px;
        font: 400 0.78125rem/1 var(--font-family);
        color: #9aa0a6;
    }
}
</style>
